import { useEffect, useState } from 'react';

interface AkamaiImageCropArgs {
  gravity?: string;
  height: number;
  width: number;
  xPosition?: number;
  yPosition?: number;
}

interface AkamaiImageRegionOfInterestCropArgs {
  height: number;
  roiHeight: number;
  roiWidth: number;
  width: number;
}

export const useCropSrcset = (
  widths: number[],
  src: string,
  imageDimensions: { height: number; width: number },
  coords: { x1: number; x2: number; y1: number; y2: number },
) => {
  const [newSrc, setNewSrc] = useState('');
  const [srcSet, setSrcSet] = useState('');

  const height = coords.y2 - coords.y1;
  const width = coords.x2 - coords.x1;
  const xPosition = coords.x1;
  const yPosition = coords.y1;

  useEffect(() => {
    const cropQuery = (cropArgs: AkamaiImageCropArgs) => {
      return `im=Crop,rect=(${cropArgs.xPosition},${cropArgs.yPosition},${cropArgs.width},${cropArgs.height})`;
    };
    // For reference, this produces e.g. https://www-storefront-int.breitling.com/api/image-proxy/www-breitling-int.eu.saleor.cloud/media/thumbnails/products/a32320101c1a1-soldier_30c5bd56_thumbnail_1024.png?im=Crop,rect=(312,728,146,41)

    const regionOfInterestCropQuery = (
      roiCropArgs: AkamaiImageRegionOfInterestCropArgs,
    ) => {
      return `;RegionOfInterestCrop,width=${roiCropArgs.width},height=${roiCropArgs.height},regionOfInterest=(${roiCropArgs.roiWidth},${roiCropArgs.roiHeight})`;
    };
    // For reference, this produces e.g. https://www-storefront-int.breitling.com/api/image-proxy/www-breitling-int.eu.saleor.cloud/media/thumbnails/products/a32320101c1a1-soldier_30c5bd56_thumbnail_1024.png?im=Crop,rect=(312,728,146,41);RegionOfInterestCrop,width=146,height=41,regionOfInterest=(73,20.5)

    const modifiedSrc = src.replace(
      'https://',
      `${process.env.NEXT_PUBLIC_URL}api/image-proxy/`,
    );

    const croppedSrc = `${modifiedSrc}?${cropQuery({
      height: height,
      width: width,
      xPosition: xPosition,
      yPosition: yPosition,
    })}`;

    setNewSrc(croppedSrc);

    const srcSet = widths
      .map((setWidth) => {
        const scaledHeight = Math.round(width * (height / width));
        return `${croppedSrc}${regionOfInterestCropQuery({
          height: scaledHeight,
          roiHeight: scaledHeight / 2,
          roiWidth: setWidth < width ? setWidth / 2 : width / 2,
          width: setWidth < width ? setWidth : width,
        })} ${width}w`;
      })
      .join(', ');

    setSrcSet(srcSet);
  }, [widths, src, imageDimensions, coords]);

  return { newSrc, srcSet };
};
